import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { LocationService } from '../../../services/location.service';
import { LocationOption } from '../../../shared/types';
import { Subject, takeUntil } from 'rxjs';

/**
 * OfferDetailsComponent displays and edits the offer configuration (type, discount, applicable car-wash packages and locations) for a campaign. It provides UI bindings for toggling its expanded state, validating and editing offer details, converting UI checkbox selections to API-friendly values, and loading available locations from a service.
 * @component
 * @selector app-offer-details
 * @example
 *   <app-offer-details
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)">
 *   </app-offer-details>
 * @input boolean isExpanded - Controls whether the offer details panel is expanded in the UI (default: true).
 * @input any campaignData - Existing campaign payload used to initialize form state (offer_type, offer_discount, offer_wash_type, locations). The component normalizes legacy formats and filters out UI-only values like "allLocations".
 * @input boolean isCompleted - Indicates whether the campaign step is completed (used to control UI/state).
 * @input boolean isSaving - When true, Done shows a spinner and is disabled while the persist is in flight.
 * @output EventEmitter<boolean> toggle - Emitted when the component's expanded/collapsed state changes; payload is the new expanded boolean.
 * @output EventEmitter<void> done - Emitted when the user finishes editing and validation passes (used to advance the editor workflow).
 * @output EventEmitter<any> dataChange - Emitted whenever the form changes with a sanitized payload for the API: { offer_type, offer_discount, offer_wash_type, locations }.
 *
 * Key methods:
 * - onOfferDetailsToggle(expanded: boolean): Emits the toggle event when the UI panel is opened/closed.
 * - onOfferDetailsDone(): Validates required fields and emits done if valid.
 * - incrementValue() / decrementValue(): Safely adjust the discountValue within 0-100 bounds.
 * - onFormChange(): Consolidates checkbox arrays and fields into API-friendly values, updates internal state, and emits dataChange.
 * - onOfferTypeChange(), onDiscountValueChange(), onCarWashTypeChange(): Field-specific handlers that validate and trigger onFormChange.
 * - validateDiscountValue(): Validates discountValue, sets isDiscountValueValid and discountValueError for UI feedback.
 * - isDiscountFieldDisabled(): Computes whether the discount input should be disabled based on required campaign metadata (name, promo code, description).
 * - onLocationChange(): Handles complex "All locations" UI behavior (select/deselect semantics), keeps previous state, and emits changes.
 * - onLocationSelectionToggle()/isLocationSelected(): Material checkbox helpers that keep `locations` as string[] (MatCheckbox ngModel is boolean-only).
 * - onCarWashTypeSelectionToggle()/isCarWashTypeSelected(): Same pattern for car wash type checkboxes.
 * - trackByCarWashType()/trackByLocation(): Track-by helpers for efficient list rendering.
 * - loadLocations(): Loads available locations from LocationService, builds locationOptions including an "All locations" option, and falls back on error.
 * - getAllIndividualLocationValues() / getClickedIndividualLocation(): Helpers to determine individual location values and the specific location clicked in a change operation.
 * - syncAllLocationsState(): Ensures "allLocations" UI state stays consistent when all individual locations are selected.
 * - normalizeLocationIds(): Maps campaign/API location payloads (string IDs or `{ id }` objects) to a string[] and rejects non-arrays.
 *
 * Important private helpers:
 * - validateAndNormalizeCarWashType(value): Accepts legacy array or string values and returns a normalized string ('any' | 'single_wash' | 'monthly_membership').
 * - isValidCarWashTypeString(value): Simple validator for allowed car-wash type strings.
 * - stringToCarWashTypesArray(value): Converts normalized string back to checkbox array for the UI.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes form state from campaignData (with normalization and fallbacks), ensures arrays are non-empty, sets previousLocations, and calls loadLocations to populate location options.
 * - ngOnChanges(): Responds to incoming campaignData changes by re-syncing form fields, normalizing wash-type representations, filtering UI-only values, and syncing "allLocations" state once options are loaded.
 * - ngOnDestroy(): Cleans up subscriptions via destroy$ to prevent memory leaks.
 */
@Component({
  selector: 'app-offer-details',
  imports: [CollapsibleSectionComponent, CommonModule, FormsModule, MatCheckboxModule],
  templateUrl: './offer-details.component.html',
  styleUrl: './offer-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OfferDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Input() isSaving = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  // Form properties
  offerType = 'flat_discount';
  discountValue = 0;

  // Validation properties
  discountValueError = '';
  isDiscountValueValid = true;

  // CWS-1110: at least one location is required. The section could be completed with none
  // selected, leaving the campaign with no campaign_locations rows — which is what
  // getCampaignByLocation (GET campaign/location/:id) INNER JOINs on, so the offer is missing
  // from that lookup entirely.
  locationError = '';
  isLocationValid = true;

  // Car wash types as arrays for Material checkboxes (UI only)
  carWashTypes: string[] = ['single_wash'];
  carWashTypeOptions = [
    { label: 'Single Wash', value: 'single_wash' },
    { label: 'Monthly Membership', value: 'monthly_membership' },
  ];

  // Internal string value for API: 'any', 'single_wash', or 'monthly_membership'
  private carWashTypeString = 'single_wash';

  // Selected location IDs for Material checkboxes (may include UI-only 'allLocations')
  locations: string[] = [];
  locationOptions: LocationOption[] = [];
  isLoadingLocations = false;
  private previousLocations: string[] = [];
  private destroy$ = new Subject<void>();

  constructor(private locationService: LocationService) {}

  /**
   * Initialize component state for offer details using existing campaignData or sensible defaults, normalize car wash types, filter out "allLocations", ensure non-empty arrays, store previous locations, and load available locations.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} Sets up offerType, discountValue, carWashTypeString, carWashTypes, locations, previousLocations and calls loadLocations().
   **/
  ngOnInit(): void {
    // Initialize with existing data if provided
    if (this.campaignData && this.campaignData.offer_wash_type) {
      this.offerType = this.campaignData.offer_type || 'flat_discount';
      this.discountValue = this.campaignData.offer_discount || 0;
      // Validate and convert string from API to checkbox array
      this.carWashTypeString = this.validateAndNormalizeCarWashType(
        this.campaignData.offer_wash_type
      );
      this.carWashTypes = this.stringToCarWashTypesArray(this.carWashTypeString);
      this.locations = this.normalizeLocationIds(this.campaignData.locations);
    } else {
      // Initialize with default values
      this.offerType = this.campaignData?.offer_type || 'flat_discount';
      this.discountValue = this.campaignData?.offer_discount || 0;
      this.carWashTypeString = 'single_wash';
      this.carWashTypes = ['single_wash'];
      this.locations = this.normalizeLocationIds(this.campaignData?.locations);
    }

    // Ensure arrays are never empty
    this.carWashTypes =
      this.carWashTypes && this.carWashTypes.length > 0 ? this.carWashTypes : ['single_wash'];
    this.locations = this.locations && this.locations.length > 0 ? this.locations : [];

    // Initialize previous locations state
    this.previousLocations = [...this.locations];

    // Load locations from service
    this.loadLocations();
  }

  /**
   * Update component fields and internal state when campaignData changes; synchronizes offer type, discount, car wash types, locations, applies sensible defaults, and triggers all-locations syncing.
   * @example
   * gOnChanges()
   * void
   * @param {void} none - This method does not accept any parameters.
   * @returns {void} Updates component instance fields in place and does not return a value.
   **/
  ngOnChanges(): void {
    // Update form fields when campaign data changes
    if (this.campaignData && this.campaignData.offer_wash_type) {
      this.offerType = this.campaignData.offer_type || 'flat_discount';
      this.discountValue = this.campaignData.offer_discount || 0;
      // Validate and convert string from API to checkbox array
      this.carWashTypeString = this.validateAndNormalizeCarWashType(
        this.campaignData.offer_wash_type
      );
      this.carWashTypes = this.stringToCarWashTypesArray(this.carWashTypeString);
      this.locations = this.normalizeLocationIds(this.campaignData.locations);

      // Update previous locations state
      this.previousLocations = [...this.locations];
    } else if (this.campaignData) {
      // Handle case where campaignData exists but offer_wash_type is missing
      this.offerType = this.campaignData.offer_type || 'flat_discount';
      this.discountValue = this.campaignData.offer_discount || 0;
      this.carWashTypeString = 'single_wash';
      this.carWashTypes = ['single_wash']; // Default to single_wash
      this.locations = this.normalizeLocationIds(this.campaignData.locations);

      // Update previous locations state
      this.previousLocations = [...this.locations];
    }

    // Ensure arrays are never empty
    this.carWashTypes =
      this.carWashTypes && this.carWashTypes.length > 0 ? this.carWashTypes : ['single_wash'];
    this.locations = this.locations && this.locations.length > 0 ? this.locations : [];

    // Sync "allLocations" state after locations are loaded
    if (this.locationOptions.length > 1) {
      // More than just "allLocations" option
      this.syncAllLocationsState();
    }
  }

  onOfferDetailsToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onOfferDetailsDone(): void {
    if (this.isSaving) {
      return;
    }

    // Validate all required fields before allowing done
    this.validateDiscountValue();
    this.validateLocations();

    if (this.isDiscountValueValid && this.isLocationValid) {
      this.done.emit();
    }
  }

  incrementValue(): void {
    if (this.discountValue < 100) {
      this.discountValue += 1;
    }
  }

  decrementValue(): void {
    if (this.discountValue > 0) {
      this.discountValue -= 1;
    }
  }

  trackByCarWashType(index: number, option: any): string {
    return option.value;
  }

  trackByLocation(index: number, option: any): string {
    return option.value;
  }

  /**
   * Normalize selected car wash types and locations, update component state, and emit the resulting offer payload.
   * @example
   * this.nFormChange()
   * // Emits via dataChange: { offer_type: 'campaign', offer_discount: 10, offer_wash_type: 'any', locations: ['loc1','loc2'] }
   * @returns {void} Emits a normalized data object (offer_type, offer_discount, offer_wash_type, locations) via the dataChange event; does not return a value.
   **/
  onFormChange(): void {
    // Convert checkbox array to string value for API
    // Always send as string: 'any', 'single_wash', or 'monthly_membership'
    let packagesValue: string;

    if (!Array.isArray(this.carWashTypes)) {
      this.carWashTypes = ['single_wash'];
    }
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }

    if (
      this.carWashTypes.length === 2 &&
      this.carWashTypes.includes('single_wash') &&
      this.carWashTypes.includes('monthly_membership')
    ) {
      packagesValue = 'any';
    } else if (this.carWashTypes.includes('monthly_membership')) {
      packagesValue = 'monthly_membership';
    } else if (this.carWashTypes.includes('single_wash')) {
      packagesValue = 'single_wash';
    } else if (this.carWashTypeString && this.isValidCarWashTypeString(this.carWashTypeString)) {
      packagesValue = this.carWashTypeString;
    } else {
      packagesValue = 'single_wash';
    }

    // Update internal string value
    this.carWashTypeString = packagesValue;

    // Filter out "allLocations" from locations array before emitting
    // "allLocations" is only used for UI state, not for API calls
    const locationsForApi = this.locations.filter(location => location !== 'allLocations');

    const data = {
      offer_type: this.offerType,
      offer_discount: this.discountValue,
      offer_wash_type: packagesValue, // Always send as string
      locations: locationsForApi,
    };
    this.dataChange.emit(data);
  }

  onOfferTypeChange(): void {
    this.onFormChange();
  }

  onDiscountValueChange(): void {
    this.validateDiscountValue();
    this.onFormChange();
  }

  onCarWashTypeChange(): void {
    // Ensure carWashTypes array is never empty
    if (!Array.isArray(this.carWashTypes) || this.carWashTypes.length === 0) {
      this.carWashTypes = ['single_wash'];
    }
    this.onFormChange();
  }

  isCarWashTypeSelected(carWashTypeValue: string): boolean {
    return Array.isArray(this.carWashTypes) && this.carWashTypes.includes(carWashTypeValue);
  }

  onCarWashTypeSelectionToggle(carWashTypeValue: string, checked: boolean): void {
    if (!Array.isArray(this.carWashTypes)) {
      this.carWashTypes = [];
    }
    if (checked) {
      if (!this.carWashTypes.includes(carWashTypeValue)) {
        this.carWashTypes = [...this.carWashTypes, carWashTypeValue];
      }
    } else {
      this.carWashTypes = this.carWashTypes.filter(value => value !== carWashTypeValue);
    }
    this.onCarWashTypeChange();
  }

  isLocationSelected(locationValue: string): boolean {
    return Array.isArray(this.locations) && this.locations.includes(locationValue);
  }

  /**
   * Update the selected locations array from a Material checkbox toggle, then run all-locations sync logic.
   * MatCheckbox ngModel is boolean-only; binding ngModel to `locations` would replace the string[] with true/false.
   */
  onLocationSelectionToggle(locationValue: string, checked: boolean): void {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    if (checked) {
      if (!this.locations.includes(locationValue)) {
        this.locations = [...this.locations, locationValue];
      }
    } else {
      this.locations = this.locations.filter(value => value !== locationValue);
    }
    this.onLocationChange();
  }

  /**
   * Normalize campaign/API location payloads into a string[] of IDs.
   * Accepts string IDs or location objects with `id`; drops UI-only `allLocations` and non-array values.
   */
  private normalizeLocationIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((loc: unknown) => {
        if (typeof loc === 'string') {
          return loc;
        }
        if (loc && typeof loc === 'object' && 'id' in loc) {
          const id = (loc as { id: unknown }).id;
          if (typeof id === 'string') {
            return id;
          }
          if (id != null && (typeof id === 'number' || typeof id === 'boolean')) {
            return String(id);
          }
        }
        return '';
      })
      .filter((id): id is string => !!id && id !== 'allLocations');
  }

  /**
   * Validates and normalizes car wash type string from API
   * Returns valid string: 'any', 'single_wash', or 'monthly_membership'
   */
  private validateAndNormalizeCarWashType(value: any): string {
    // If it's already a valid string, return it
    if (typeof value === 'string' && this.isValidCarWashTypeString(value)) {
      return value;
    }

    // Handle array case (legacy support)
    if (Array.isArray(value)) {
      if (
        value.length === 2 &&
        value.includes('single_wash') &&
        value.includes('monthly_membership')
      ) {
        return 'any';
      } else if (value.includes('monthly_membership')) {
        return 'monthly_membership';
      } else if (value.includes('single_wash')) {
        return 'single_wash';
      }
    }

    // Default fallback
    return 'single_wash';
  }

  /**
   * Checks if a string is a valid car wash type
   */
  private isValidCarWashTypeString(value: string): boolean {
    return value === 'any' || value === 'single_wash' || value === 'monthly_membership';
  }

  /**
   * Converts string value to checkbox array for UI
   */
  private stringToCarWashTypesArray(value: string): string[] {
    if (value === 'any') {
      return ['single_wash', 'monthly_membership'];
    } else if (value === 'monthly_membership') {
      return ['monthly_membership'];
    } else if (value === 'single_wash') {
      return ['single_wash'];
    }
    // Default fallback
    return ['single_wash'];
  }

  /**
   * Validates the component's discountValue and updates isDiscountValueValid and discountValueError accordingly.
   * @example
   * this.validateDiscountValue()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Updates internal state; does not return a value.
   **/
  validateDiscountValue(): void {
    if (
      this.discountValue === null ||
      this.discountValue === undefined ||
      this.discountValue === 0
    ) {
      this.isDiscountValueValid = false;
      this.discountValueError = 'Discount value is required and must be greater than 0';
    } else if (this.discountValue < 0 || this.discountValue > 100) {
      this.isDiscountValueValid = false;
      this.discountValueError = 'Discount value must be between 1 and 100';
    } else {
      this.isDiscountValueValid = true;
      this.discountValueError = '';
    }
  }

  /**
   * Validates that at least one offer location is selected and updates isLocationValid and
   * locationError for the template. `allLocations` is a UI-only marker, so it never counts
   * on its own — the API payload is built from the individual ids beside it.
   */
  validateLocations(): void {
    const selected = Array.isArray(this.locations)
      ? this.locations.filter(location => location !== 'allLocations')
      : [];
    if (selected.length === 0) {
      this.isLocationValid = false;
      this.locationError = 'Select at least one location for this offer';
      return;
    }
    this.isLocationValid = true;
    this.locationError = '';
  }

  isDiscountFieldDisabled(): boolean {
    // Disable the discount field if prerequisite offer data is missing
    return (
      !this.campaignData ||
      !this.campaignData.offer_name ||
      this.campaignData.offer_name.trim() === '' ||
      !this.campaignData.promo_code ||
      this.campaignData.promo_code.trim() === '' ||
      !this.campaignData.offer_description ||
      this.campaignData.offer_description.trim() === ''
    );
  }

  /**
   * Synchronize "All locations" toggle with individual location selections, update previousLocations, and trigger form change.
   * @example
   * nLocationChange()
   * undefined
   * @returns {void} Updates the component's locations and previousLocations state and calls onFormChange.
   */
  onLocationChange(): void {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    if (!Array.isArray(this.previousLocations)) {
      this.previousLocations = [];
    }

    // Check if "All locations" was just clicked (toggled off)
    const wasAllLocationsSelected = this.previousLocations.includes('allLocations');
    const isAllLocationsSelected = this.locations.includes('allLocations');

    // Check if user clicked on an individual location while "All locations" was selected
    const wasAllLocationsOnlySelected =
      wasAllLocationsSelected &&
      this.previousLocations.length > 1 &&
      this.previousLocations.filter(loc => loc !== 'allLocations').length > 0;

    const clickedIndividualLocation = this.getClickedIndividualLocation();

    if (wasAllLocationsOnlySelected && clickedIndividualLocation) {
      // User clicked on an individual location while "All locations" was selected
      // Remove "All locations" and the clicked location, keep all other individual locations
      const allIndividualLocations = this.getAllIndividualLocationValues();
      this.locations = allIndividualLocations.filter(loc => loc !== clickedIndividualLocation);
    } else if (wasAllLocationsSelected && !isAllLocationsSelected) {
      // "All locations" was deselected, deselect all individual locations too
      this.locations = [];
    } else if (!wasAllLocationsSelected && isAllLocationsSelected) {
      // "All locations" was just selected, select all individual locations
      const allIndividualLocations = this.getAllIndividualLocationValues();
      this.locations = ['allLocations', ...allIndividualLocations];
    } else if (isAllLocationsSelected) {
      // "All locations" is still selected, ensure all individual locations are selected
      const allIndividualLocations = this.getAllIndividualLocationValues();
      this.locations = ['allLocations', ...allIndividualLocations];
    } else {
      // Handle individual location changes
      // If any individual location is deselected, remove "All locations"
      if (this.locations.includes('allLocations')) {
        this.locations = this.locations.filter(location => location !== 'allLocations');
      }

      // Check if all individual locations are selected, then auto-select "All locations"
      const allIndividualLocations = this.getAllIndividualLocationValues();
      const selectedIndividualLocations = this.locations.filter(
        location => location !== 'allLocations'
      );

      if (
        allIndividualLocations.length > 0 &&
        allIndividualLocations.every(location => selectedIndividualLocations.includes(location))
      ) {
        this.locations = ['allLocations', ...selectedIndividualLocations];
      }
    }

    // Update previous state for next change
    this.previousLocations = [...this.locations];

    this.validateLocations();
    this.onFormChange();
  }

  private getAllIndividualLocationValues(): string[] {
    return this.locationOptions
      .filter(option => option.value !== 'allLocations')
      .map(option => option.value);
  }

  /**
   * Determine which individual location was clicked by comparing current and previous location selections.
   * @example
   * getClickedIndividualLocation()
   * 'location-id-123'
   * @returns {string|null} The id or name of the single location that was added or removed, or null if none or ambiguous.
   */
  private getClickedIndividualLocation(): string | null {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    if (!Array.isArray(this.previousLocations)) {
      this.previousLocations = [];
    }

    // Find which individual location was clicked by comparing current and previous states
    const currentIndividualLocations = this.locations.filter(loc => loc !== 'allLocations');
    const previousIndividualLocations = this.previousLocations.filter(
      loc => loc !== 'allLocations'
    );

    // Find locations that were added or removed
    const addedLocations = currentIndividualLocations.filter(
      loc => !previousIndividualLocations.includes(loc)
    );
    const removedLocations = previousIndividualLocations.filter(
      loc => !currentIndividualLocations.includes(loc)
    );

    // If only one location was added, that's the clicked one
    if (addedLocations.length === 1) {
      return addedLocations[0];
    }

    // If only one location was removed, that's the clicked one (for deselection)
    if (removedLocations.length === 1) {
      return removedLocations[0];
    }

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load location options from the LocationService and update component state with a list that always includes an "All locations" option.
   * @example
   * loadLocations()
   * undefined
   * @returns {{void}} Does not return a value; updates component state (isLoadingLocations, locationOptions) and synchronizes "allLocations" state.
   **/
  private loadLocations(): void {
    this.isLoadingLocations = true;

    this.locationService
      .getLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isLoadingLocations = false;

          if (response.status && response.data && Array.isArray(response.data)) {
            // Add "All locations" option first
            this.locationOptions = [
              { label: 'All locations', value: 'allLocations' },
              ...response.data
                .filter((location: any) => location.status) // Only show active locations
                .map((location: any) => ({
                  label: location.branch,
                  value: location.id,
                })),
            ];

            // After loading locations, sync "allLocations" state if needed
            this.syncAllLocationsState();
          } else {
            // Fallback to default options if API fails
            this.locationOptions = [{ label: 'All locations', value: 'allLocations' }];
          }
        },
        error: (error: any) => {
          this.isLoadingLocations = false;
          console.error('Error loading locations:', error);
          // Fallback to default options on error
          this.locationOptions = [{ label: 'All locations', value: 'allLocations' }];
        },
      });
  }

  // Sync "allLocations" state: if all individual locations are selected, add "allLocations" to UI array
  /**
   * Ensure the component's locations array includes 'allLocations' when every individual location is selected.
   * @example
   * this.syncAllLocationsState()
   * undefined
   * @returns {void} Updates this.locations and this.previousLocations in-place; no value is returned.
   **/
  private syncAllLocationsState(): void {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }

    const allIndividualLocations = this.getAllIndividualLocationValues();
    const selectedIndividualLocations = this.locations.filter(
      location => location !== 'allLocations'
    );

    if (
      allIndividualLocations.length > 0 &&
      allIndividualLocations.every(location => selectedIndividualLocations.includes(location)) &&
      !this.locations.includes('allLocations')
    ) {
      this.locations = ['allLocations', ...selectedIndividualLocations];
      this.previousLocations = [...this.locations];
    }
  }
}
